import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import SingleApplicantTopHeader from './SingleApplicantTopHeader';
import OnlineForm from '../Applicants/OnlineForm/OnlineForm';
import StatusContainer from '../Custom/StatusContainer';
import styles from './InterestedPdfExport.module.css';

const valueOrNA = (value) =>
  value == null || (typeof value === 'string' && !value.trim()) ? 'N/A' : value;

// Use the actual Online Form components so its fields and conditional display
// stay in sync with the page, without mounting tabs or fetching more data.
export const InterestedPdfDocument = ({ applicant }) => {
  const person = new Proxy(applicant.intersted, {
    get: (target, key) => valueOrNA(target[key]),
  });
  return (
    <div className={styles.document}>
      <SingleApplicantTopHeader data={person} rightComponent={[]} />
      <div className={styles.summary}>
        <div><strong>Field of Interest:</strong> {person.fieldOfInterest_Display}</div>
        <div><strong>Current Status:</strong> <StatusContainer status={person.statusText} /></div>
      </div>
      <OnlineForm applicant={{ ...applicant, intersted: person }} isApplicant={false} />
    </div>
  );
};

export const interestedPdfFilename = (person) => {
  const part = (value) => String(valueOrNA(value))
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_').replace(/\s+/g, '_');
  return `Interested_${part(person.firstName)}_${part(person.lastName)}.pdf`;
};

export async function downloadInterestedPdf(applicant) {
  if (!applicant?.intersted) throw new Error('Interested details are not loaded');
  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import('html2canvas'), import('jspdf'),
  ]);
  const host = document.createElement('div');
  host.style.cssText = 'position:absolute;left:-10000px;top:0;width:1000px;pointer-events:none;';
  host.setAttribute('aria-hidden', 'true');
  host.innerHTML = renderToStaticMarkup(<InterestedPdfDocument applicant={applicant} />);
  document.body.appendChild(host);
  try {
    if (document.fonts?.ready) await document.fonts.ready;
    await Promise.all(Array.from(host.querySelectorAll('img')).map(async (img) => {
      if (img.decode) await img.decode();
    }));
    // The form uses NA plus an icon for missing fields; exports use plain N/A.
    host.querySelectorAll('span').forEach((span) => {
      if (/^NA\s*$/.test(span.textContent)) span.textContent = 'N/A';
    });
    host.querySelectorAll('a[href="tel:N/A"]').forEach((link) => { link.textContent = 'N/A'; });
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const margin = 12;
    const width = pdf.internal.pageSize.getWidth() - margin * 2;
    const height = pdf.internal.pageSize.getHeight() - margin * 2;
    let y = margin;
    const root = host.firstElementChild;
    const form = root.lastElementChild;
    const sections = [root.children[0], root.children[1], ...Array.from(form.children)];
    for (const section of sections) {
      if (!section.getBoundingClientRect().height) continue;
      const canvas = await html2canvas(section, {
        scale: 2, backgroundColor: '#ffffff', windowWidth: 1400, windowHeight: 1000,
        logging: false,
      });
      // Keep each section intact. Exceptionally tall sections shrink to one A4
      // content area, so neither a label nor its value is cut across pages.
      const renderedHeight = canvas.height * width / canvas.width;
      const ratio = Math.min(1, height / renderedHeight);
      const blockHeight = renderedHeight * ratio;
      if (y > margin && y + blockHeight > margin + height) {
        pdf.addPage();
        y = margin;
      }
      pdf.addImage(canvas, 'PNG', margin, y, width * ratio, blockHeight);
      y += blockHeight + 5;
    }
    pdf.save(interestedPdfFilename(applicant.intersted));
  } finally {
    host.remove();
  }
}
